// PeptideVault v6.5 — pure schema migration registry.
// No UI/global state dependencies: safe to run against backups before hydration.

const CURRENT_SCHEMA_VERSION = 6
const DATA_ARRAY_KEYS = [
  'peptides','documents','storageLocations','injections','healthData','notes','labResults','supplements','users',
  'peptideLibrary','changeLog','customCategories','refillLog','injectionSupplies','peptidePens','vendors','cycleTimelines','blends',
  'protocols','scenarios','inventoryReservations','regimenVersions','coas','labMarkerDictionary','savedViews','trash','snapshots',
  'reportPresets','masterPlanRules','protocolDependencies','vaultPositions','vaultAssignments','vaultMovements','vaultReconciliations',
  'protocolRequirements','mobilePickingSheets','supplementLabelReviews','preparationBatches'
]

const SCHEMA_MIGRATIONS = [
  { from: 0, to: 1, migrate(data) {
      data = data || {}
      DATA_ARRAY_KEYS.forEach(k => { if (!Array.isArray(data[k])) data[k] = [] })
      return data
    }
  },
  { from: 1, to: 2, migrate(data) {
      data.inventoryReservations = (data.inventoryReservations || []).map(r => {
        const out = { ...r }
        out.qty = Math.max(0, Number(r.qty != null ? r.qty : (r.quantity != null ? r.quantity : 1)) || 0)
        delete out.quantity
        if (out.skuId == null) out.skuId = ''
        return out
      })
      data.protocolDependencies = (data.protocolDependencies || []).map(dep => {
        const out = { ...dep }
        out.offsetDays = isFinite(Number(out.offsetDays)) ? Number(out.offsetDays) : 0
        if (!out.relatedProtocolId) out.relatedProtocolId = ''
        if (!out.relatedCompound) out.relatedCompound = ''
        return out
      })
      return data
    }
  },
  { from: 2, to: 3, migrate(data) {
      // Peptide mapped storage is deliberately separate from storageLocations,
      // which continues to back injection-supply storage without alteration.
      data.vaultPositions = Array.isArray(data.vaultPositions) ? data.vaultPositions : []
      data.vaultAssignments = Array.isArray(data.vaultAssignments) ? data.vaultAssignments : []
      data.vaultMovements = Array.isArray(data.vaultMovements) ? data.vaultMovements : []
      data.vaultReconciliations = Array.isArray(data.vaultReconciliations) ? data.vaultReconciliations : []
      return data
    }
  },
  { from: 3, to: 4, migrate(data) {
      // Restore authoritative labeled vial strengths from the reviewed inventory
      // export. Tested COA concentration remains a separate analytical field.
      const strengthBySku = {
        '2025K516-12':80,'klow80':80,'2026A512-01':60,'2025111060':60,'PGB-T60-3':60,'2025121020':20,'Sams_T100':100,'POT-o-GLP2300326':30,'JKL_20mg_TR20':20,'410004':20,'EGB-0526-TR20':20,'JEC-TIRZ75-T100fail':75,'LY3298176-JEC':70,
        'PGB-MOTS-3':10,'PGB-SS31-2':25,'SS3150260115-50':50,'PGB-SNAP-1':20,'PGB-GHKCU-1':1,'Amin2602110133':10,'CJC+IPA10260124':10,'PGB-EPI10-2':10,'SUPERPANDA-EPT10-GREEN-SLVR-0326':10,'PGB-NAEPI-2':10,'SAMLAB-BRN-0426':10,'-EGB-WWB0825BPC-10':10,'SAMLAB-BLU-0426':10,'RO-A20160129-WhiteCap':50,'PGB-GHK50-4':50,
        'Blk Cap 022826':10,'JKLTSM20':20,'26030343':10,'Tesa-260610':20,'KP300114':30,'PGB-KPV-3':10,'PBD-KPV10':10,'TrvKPV30-HOLD':30,'IPA25MAR2026':4.45,'PBD-IPA5-RED':5,'SAMLABS-pink-100526-NA':10,'PGB-NASLK-5':10,'OBNight-BLUx2-0426':10,'5AM1MQERP':5,'JKL_10mg_10AM':10,'990061':10,'-EGB-WWB1125AM-50':50,
        'UGPEPS_24IU_HGH':24,'UPP_HGH10':10,'JKL-HGH-24IU':24,'Uzbek_42IU_HGH':42,'PGB-VIP-1':5,'SAMLABS-yellow-100526':10,'PGB-DS5-4':5,'BRON20-0228':20,'EGB-0526-SUR10':10,'PGB-SUR12-1':12,'EGB-WWB1025GLTA-1500':1500,'PGB-ARA-2':16,'PBD-ARA290-30-CHP-BLU':30,'PSARA16-1':16,'PGB-NASEM-5':10,'WombatOrange-04260626':20,
        'JKL_10mg_KS10':10,'990049':10,'JKL_5mg_OT5':5,'990079':5,'JKL_5mg_G25':5,'990040':5,'SAMLAB-BananaCap-0526':10,'EGB-0626-BB20':20,'GCRY43RG1NF2':14,'PGB-PT141-3':12,'WombatCarta062626':18
      }
      let restored = 0
      ;(data.peptides || []).forEach(peptide => (peptide.skus || []).forEach(sku => {
        if (Number(sku.vialStrengthMg) > 0) return
        const identifiers = [sku.lotNumber, sku.barcode, sku.id].filter(Boolean)
        const matched = identifiers.find(value => strengthBySku[value] != null)
        if (matched) { sku.vialStrengthMg = strengthBySku[matched]; restored++ }
      }))
      if (restored) data._pendingLocalDataMigration = `restored ${restored} labeled vial strengths`
      return data
    }
  },
  { from: 4, to: 5, migrate(data) {
      // v7.8.43 starts the simplified shopping workflow with a clean cart.
      // Retain completed/picked/in-use rows as audit history, but remove every
      // outstanding selection so stale plans cannot reappear through sync.
      const before = Array.isArray(data.inventoryReservations) ? data.inventoryReservations : []
      const retained = before.filter(row => {
        const status = String(row && row.workflowStatus || '').toLowerCase()
        return status === 'picked' || status === 'completed' || status === 'in_use' || Boolean(row && row.pickConfirmedAt)
      })
      const removed = before.length - retained.length
      data.inventoryReservations = retained
      if (removed) {
        const message = `cleared ${removed} outstanding shopping selection${removed === 1 ? '' : 's'}`
        data._pendingLocalDataMigration = data._pendingLocalDataMigration
          ? `${data._pendingLocalDataMigration}; ${message}`
          : message
      }
      return data
    }
  },
  { from: 5, to: 6, migrate(data) {
      // v9.0 adds reviewable preparation and label-intelligence workflows.
      // These domains are additive; no existing inventory or history is altered.
      data.protocolRequirements = Array.isArray(data.protocolRequirements) ? data.protocolRequirements : []
      data.mobilePickingSheets = Array.isArray(data.mobilePickingSheets) ? data.mobilePickingSheets : []
      data.supplementLabelReviews = Array.isArray(data.supplementLabelReviews) ? data.supplementLabelReviews : []
      data.preparationBatches = Array.isArray(data.preparationBatches) ? data.preparationBatches : []
      return data
    }
  }
]

function runSchemaMigrations(input) {
  let data = input && typeof input === 'object' ? input : {}
  let version = Number(data._schemaVersion || 0) || 0
  let safety = 0
  while (version < CURRENT_SCHEMA_VERSION && safety++ < 20) {
    const migration = SCHEMA_MIGRATIONS.find(m => m.from === version)
    if (!migration) throw new Error(`No schema migration path from v${version} to v${CURRENT_SCHEMA_VERSION}`)
    data = migration.migrate(data)
    version = migration.to
    data._schemaVersion = version
  }
  if (version > CURRENT_SCHEMA_VERSION) throw new Error(`Data schema v${version} is newer than this app (v${CURRENT_SCHEMA_VERSION})`)
  data._schemaVersion = CURRENT_SCHEMA_VERSION
  return data
}

export { CURRENT_SCHEMA_VERSION, DATA_ARRAY_KEYS, SCHEMA_MIGRATIONS, runSchemaMigrations }
